import { useState, useEffect, Fragment } from "react";
import { api } from "@/lib/api";
import { monthOptions, monthLabel } from "@/lib/month";
import { exportTeamCsv, exportTeamPdf } from "@/lib/teamPerfExport";
import { Button } from "@/components/ui/button";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Gauge, Loader2, ChevronDown, ChevronRight, Info, FileDown, FileSpreadsheet } from "lucide-react";

const currentMonth = () => new Date(Date.now() + 7 * 3600 * 1000).toISOString().slice(0, 7);

const GRADE_CLS = {
  Excellent: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  Good: "bg-teal-500/15 text-teal-400 border-teal-500/30",
  "Needs improvement": "bg-amber-500/15 text-amber-400 border-amber-500/30",
  Poor: "bg-rose-500/15 text-rose-400 border-rose-500/30",
};

function ScoreBadge({ score, grade }) {
  if (score === null || score === undefined) return <span className="text-xs text-slate-500">—</span>;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${GRADE_CLS[grade]}`}>
      {score} · {grade}
    </span>
  );
}

function OpenTasks({ list, dark }) {
  if (!list.length) return <p className="text-xs text-slate-500 italic">Semua task selesai.</p>;
  return (
    <ul className="space-y-1">
      {list.map((t, i) => (
        <li key={i} className="flex items-center gap-2 text-xs">
          <span className={`h-1.5 w-1.5 rounded-full ${t.overdue ? "bg-rose-500" : "bg-amber-400"}`} />
          <span className={dark ? "text-slate-300" : "text-slate-700"}>{t.title}</span>
          {t.due_date && <span className={`font-mono ${t.overdue ? "text-rose-400" : "text-slate-500"}`}>due {t.due_date}{t.overdue ? " (overdue)" : ""}</span>}
        </li>
      ))}
    </ul>
  );
}

function Sparkline({ values, months, testId }) {
  const pts = values.map((v, i) => [i, v]).filter(([, v]) => v !== null && v !== undefined);
  if (pts.length === 0) return <span className="text-xs text-slate-500" data-testid={testId}>—</span>;
  const W = 72, H = 22, n = Math.max(values.length - 1, 1);
  const xy = pts.map(([i, v]) => [(i / n) * (W - 4) + 2, H - 2 - (v / 100) * (H - 4)]);
  const first = pts[0][1], last = pts[pts.length - 1][1];
  const color = pts.length < 2 || last === first ? "#94a3b8" : last > first ? "#10b981" : "#f43f5e";
  const tip = values.map((v, i) => `${monthLabel(months[i])}: ${v ?? "-"}`).join("\n");
  return (
    <span className="inline-flex items-center gap-1.5" title={tip} data-testid={testId}>
      <svg width={W} height={H} className="overflow-visible">
        <line x1="2" x2={W - 2} y1={H - 2 - 0.5 * (H - 4)} y2={H - 2 - 0.5 * (H - 4)} stroke="#475569" strokeDasharray="2 3" strokeWidth="0.6" />
        {xy.length > 1 && <polyline points={xy.map((p) => p.join(",")).join(" ")} fill="none" stroke={color} strokeWidth="1.6" strokeLinejoin="round" />}
        {xy.map(([x, y], i) => <circle key={i} cx={x} cy={y} r={i === xy.length - 1 ? 2.4 : 1.4} fill={color} />)}
      </svg>
      {pts.length > 1 && last !== first && (
        <span className={`text-[10px] font-mono ${last > first ? "text-emerald-500" : "text-rose-500"}`}>{last > first ? "▲" : "▼"}{Math.abs(last - first)}</span>
      )}
    </span>
  );
}

export function TeamPerformance({ dark, propertyId, propertyName }) {
  const [month, setMonth] = useState(currentMonth());
  const [rows, setRows] = useState([]);
  const [trend, setTrend] = useState({ months: [], trend: {} });
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    setLoading(true);
    const params = { month };
    if (propertyId && propertyId !== "all") params.property_id = propertyId;
    Promise.all([
      api.get("/reports/team-summary", { params }),
      api.get("/reports/team-trend", { params: { ...params, months: 6 } }),
    ])
      .then(([s, t]) => { setRows(s.data.staff); setTrend(t.data); })
      .catch(() => { setRows([]); setTrend({ months: [], trend: {} }); })
      .finally(() => setLoading(false));
  }, [month, propertyId]);

  const sub = dark ? "text-slate-400" : "text-slate-500";
  const cell = `py-2.5 px-2 text-right font-mono ${dark ? "text-slate-300" : "text-slate-700"}`;

  return (
    <div className={`rounded-2xl border p-5 mb-6 ${dark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`} data-testid="team-performance-card">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-1">
        <h2 className={`font-display font-semibold text-lg flex items-center gap-2 ${dark ? "text-white" : "text-slate-900"}`}>
          <Gauge className="h-5 w-5 text-[#D96635]" /> Team performance
        </h2>
        <div className="flex items-center gap-2 flex-wrap">
          <Button size="sm" variant="outline" disabled={loading || !rows.length} onClick={() => exportTeamCsv(rows, month)}
            className={dark ? "bg-slate-800 border-slate-700 text-slate-200 hover:bg-slate-700" : ""} data-testid="team-perf-export-csv">
            <FileSpreadsheet className="h-4 w-4" /> CSV
          </Button>
          <Button size="sm" variant="outline" disabled={loading || !rows.length} onClick={() => exportTeamPdf(rows, month, propertyName)}
            className={dark ? "bg-slate-800 border-slate-700 text-slate-200 hover:bg-slate-700" : ""} data-testid="team-perf-export-pdf">
            <FileDown className="h-4 w-4" /> PDF
          </Button>
          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger className={`w-44 ${dark ? "bg-slate-800 border-slate-700 text-slate-200" : ""}`} data-testid="team-perf-month"><SelectValue /></SelectTrigger>
            <SelectContent>
              {monthOptions(11, 1).map((m) => <SelectItem key={m} value={m}>{monthLabel(m)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <p className={`text-xs mb-4 flex items-center gap-1.5 ${sub}`}>
        <Info className="h-3.5 w-3.5" /> Score = % task selesai (completion rate) · 85+ Excellent · 70+ Good · 50+ Needs improvement · &lt;50 Poor
      </p>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-[#D96635]" /></div>
      ) : rows.length === 0 ? (
        <p className={`text-sm text-center py-8 ${sub}`}>Belum ada data staff.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[960px]" data-testid="team-perf-table">
            <thead>
              <tr className={`text-[11px] uppercase tracking-wide border-b ${dark ? "border-slate-800" : "border-slate-200"} ${sub}`}>
                <th className="text-left py-2 px-2 font-medium">Staff</th>
                <th className="text-right py-2 px-2 font-medium">Task</th>
                <th className="text-right py-2 px-2 font-medium">Selesai</th>
                <th className="text-right py-2 px-2 font-medium">Belum</th>
                <th className="text-right py-2 px-2 font-medium">Overdue</th>
                <th className="text-right py-2 px-2 font-medium">On-time</th>
                <th className="text-right py-2 px-2 font-medium">Hadir</th>
                <th className="text-right py-2 px-2 font-medium">Telat</th>
                <th className="text-right py-2 px-2 font-medium">Cuti</th>
                <th className="text-right py-2 px-2 font-medium">Jam kerja</th>
                <th className="text-right py-2 px-2 font-medium">Rata²/hari</th>
                <th className="text-right py-2 px-2 font-medium">Score</th>
                <th className="text-left py-2 px-2 font-medium">Trend 6 bln</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const t = r.tasks, a = r.attendance, expanded = open === r.staff_id;
                return (
                  <Fragment key={r.staff_id}>
                    <tr
                      onClick={() => setOpen(expanded ? null : r.staff_id)}
                      className={`border-b cursor-pointer transition-colors ${dark ? "border-slate-800/70 hover:bg-slate-800/40" : "border-slate-100 hover:bg-slate-50"}`}
                      data-testid={`team-perf-row-${r.staff_id}`}
                    >
                      <td className={`py-2.5 px-2 font-medium whitespace-nowrap ${dark ? "text-slate-100" : "text-slate-900"}`}>
                        <span className="inline-flex items-center gap-1">
                          {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />} {r.name}
                        </span>
                      </td>
                      <td className={cell}>{t.assigned}</td>
                      <td className={`${cell} text-emerald-500`}>{t.done}</td>
                      <td className={`${cell} text-amber-500`}>{t.open}</td>
                      <td className={`${cell} ${t.overdue ? "text-rose-500" : ""}`}>{t.overdue}</td>
                      <td className={cell}>{t.on_time_pct === null ? "—" : `${t.on_time_pct}%`}</td>
                      <td className={cell}>{a.days_present}</td>
                      <td className={`${cell} ${a.late_count ? "text-amber-500" : ""}`}>{a.late_count}</td>
                      <td className={cell}>{a.leave_days}</td>
                      <td className={cell}>{a.total_hours}j</td>
                      <td className={cell}>{a.avg_hours}j</td>
                      <td className="py-2.5 px-2 text-right" data-testid={`team-perf-score-${r.staff_id}`}><ScoreBadge score={r.score} grade={r.grade} /></td>
                      <td className="py-2.5 px-2"><Sparkline values={trend.trend[r.staff_id] || []} months={trend.months} testId={`team-perf-trend-${r.staff_id}`} /></td>
                    </tr>
                    {expanded && (
                      <tr className={dark ? "bg-slate-800/30" : "bg-slate-50"}>
                        <td colSpan={13} className="px-6 py-3">
                          <p className={`text-[11px] uppercase tracking-wide mb-2 ${sub}`}>Task belum selesai ({t.open})</p>
                          <OpenTasks list={t.open_list} dark={dark} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
